import { _ } from "@icc/shared/helpers";
import { AppointmentStatus } from "./appointment-statuses";

export interface ComplaintAppointment {
    /**
     * Numer porządkowy
     */
    id?: number;

    /**
     * Unikalny numer w formacie UUID v4
     */
    uuid: string;

    /**
     * Uuid dokumentu
     */
    documentId: string;

    /**
     * Czy wizyta została usunięta
     */
    isDeleted?: boolean;

    /**
     * Czas utworzenia wizyty
     */
    created?: Date;

    /**
     * Czas ostatniej modyfikacji wizyty
     */
    modified?: Date;

    /**
     * Czas utworzenia wizyty na serwerze
     */
    createdServer?: Date;

    /**
     * Czas modyfikacji wizyty na serwerze
     */
    modifiedServer?: Date;

    /**
     * Czas usunięcia wizyty
     */
    deletedAt?: Date;

    /**
     * Identyfikator klienta (przeglądarki), w którym powstała wizyta
     */
    machineId?: string;

    /**
     * Data wizyty
     */
    date?: Date | null;

    /**
     * Godzina wizyty
     */
    time?: string | null;

    /**
     * Przewidywany czas trwania wizyty
     */
    scheduledTime?: number | null;

    /**
     * Wykorzystany czas
     */
    timeUsed?: number | null;

    /**
     * Status wizyty
     */
    status: AppointmentStatus;

    /**
     * Komentarz
     */
    comment?: string;

    /**
     * Id firmy przypisanej do wizyty
     */
    assignedCompanyId?: number | null;

    /**
     * Id użytkownika przypisanego do wizyty
     */
    assignedUserId?: number | null;

    /** Czy oczekuje na zapis */
    $dirty?: boolean;

    /** Indeks wizyty unikalny w obrębie danej reklamacji */
    internalIndex?: number;

    /**
     * Pola dotyczące protokołu
     */

    /**
     * Data utworzenia protokołu
     */
    protocolDate?: Date | null;

    /**
     * Uwagi serwisanta
     */
    protocolNotes?: string;

    /**
     * Wymienione części/czynności serwisowe
     */
    protocolChangedPartsOrActions?: string;

    /**
     * Typ naprawy
     */
    paidService?: boolean;

    /**
     * Kwota naprawy
     */
    repairCost?: number | null;

    /**
     * Status prac
     */
    workStatus?: 'completed' | 'inProgress' | 'rejected';

    /**
     * Jeżeli status prac jest 'inProgress', co jeszcze jest do zrobienia
     */
    workToDo?: string;

    /**
     * Uwagi użytkownika
     */
    protocolUserNotes?: string;

    /**
     * Ocena serwisu
     */
    serviceRating?: number;

    /**
     * Podpis protokołu klienta
     */
    clientSignatureImage?: string | null;

    /**
     * Podpis protokołu serwisanta
     */
    servicemanSignatureImage?: string | null;

    /**
     * Zgodność z zamówieniem
     */
    orderCompliance?: boolean;

    /**
     * Czy skrzydło posiada uszkodzenia jakiekolwiek? (jeśli tak, to należy wykonać zdjęcia)
     */
    wingDamages?: boolean;

    /**
     * Czy jest zadaszenie?
     */
    roof?: boolean;

    /**
     * Poprawne kluczenie drzwi - sprawdzenie
     */
    properDoorLocking?: boolean;

    /**
     * Regulacja drzwi
     */
    doorAdjustment?: boolean;

    /**
     * Pomiar drzwi (pion ościeżnicy / wymiary skrzydła / ugięcie skrzydła / wybrzuszenie)
     */
    doorMeasurement?: boolean;

    /**
     * Czy protokół PDF został wysłany do klienta
     */
    protocolPdfSentToClient?: boolean;

    /**
     * W jakim dniu protokół PDF został wysłany do klienta
     */
    protocolPdfSentOn?: Date | null;
}

export type ComplaintAppointmentDTO = Omit<
    ComplaintAppointment,
    'createdServer' | '$dirty'
    >;

export const ComplaintAppointmentNonReadableProps = [
    'id',
    'uuid',
    'documentId',
    'created',
    'modified',
    'createdServer',
    'modifiedServer',
    'deletedAt',
    'machineId',
    '$dirty',
    'internalIndex',
    'protocolNotes',
    'protocolChangedPartsOrActions',
    'paidService',
    'repairCost',
    'workStatus',
    'workToDo',
    'protocolUserNotes',
    'serviceRating',
    'orderCompliance',
    'wingDamages',
    'roof',
    'properDoorLocking',
    'doorAdjustment',
    'doorMeasurement',
    'protocolPdfSentToClient',
] as const satisfies readonly (keyof ComplaintAppointment)[];

export const ComplaintAppointmentReadablePropsNames: Record<Exclude<keyof ComplaintAppointment, typeof ComplaintAppointmentNonReadableProps[number]>, string> = {
    isDeleted: _('COMPLAINT_APPOINTMENT|Usunięta'),
    date: _('COMPLAINT_APPOINTMENT|Data wizyty'),
    time: _('COMPLAINT_APPOINTMENT|Godzina wizyty'),
    scheduledTime: _('COMPLAINT_APPOINTMENT|Przewidywany czas trwania wizyty'),
    timeUsed: _('COMPLAINT_APPOINTMENT|Wykorzystany czas'),
    status: _('COMPLAINT_APPOINTMENT|Status'),
    comment: _('COMPLAINT_APPOINTMENT|Komentarz'),
    assignedCompanyId: _('COMPLAINT_APPOINTMENT|Id firmy przypisanej do wizyty'),
    assignedUserId: _('COMPLAINT_APPOINTMENT|Id użytkownika przypisanego do wizyty'),
    protocolDate: _('COMPLAINT_APPOINTMENT|Data utworzenia protokołu'),
    clientSignatureImage: _('COMPLAINT_APPOINTMENT|Podpis protokołu klienta'),
    servicemanSignatureImage: _('COMPLAINT_APPOINTMENT|Podpis protokołu serwisanta'),
    protocolPdfSentOn: _('COMPLAINT_APPOINTMENT|Protokół PDF został wysłany do klienta'),
};

export interface ComplaintAppointmentWithComplaintDetails extends ComplaintAppointmentDTO {
    documentNumber?: string;
    clientName?: string;
    installationAddress?: string;
    locationSource?: 'address' | 'manual';
    locationPartialMatchConfirmed?: boolean;
    locationDescription?: string;
    latitude?: number;
    longitude?: number;
    companyName?: string;
    distributorCompanyName?: string;
    authorizedRetailerCompanyName?: string;
    complaintOrderNumber?: string;
    distributorOrderNumbers?: string;
}
